import { FluviusMandatesError } from '../api/mandates';
import { SessionExpiredError } from './sessionExpiredError';

/** What the Fluvius Data Manager shows for a failed call. */
export interface FluviusErrorState {
  message: string;
  /** True when the only fix is logging in again — the UI then offers a log-in action. */
  isSessionExpired: boolean;
}

interface MapFluviusErrorOptions {
  /**
   * Map 400 → errorInvalidCompany and 404 → errorCompanyMissingKbo. Only GET /mandates uses
   * those statuses to mean "bad company selection" / "company has no KBO"; on the link and
   * send-link calls a 400/404 means something else (request validation, proxied Fluvius
   * status, "Company not found"), so they fall through to the backend message instead.
   */
  hasCompanyStatusMapping?: boolean;
}

/**
 * One mapping for every Fluvius Data Manager call (list mandates, create link, send link).
 * SendMandateLinkAllFailedError is NOT handled here — the caller renders it as an outcome.
 */
export const mapFluviusError = (
  err: unknown,
  t: (key: string) => string,
  fallbackKey: string,
  { hasCompanyStatusMapping = false }: MapFluviusErrorOptions = {}
): FluviusErrorState => {
  const plain = (message: string): FluviusErrorState => ({ message, isSessionExpired: false });

  if (err instanceof SessionExpiredError) {
    return { message: t('errorSessionExpired'), isSessionExpired: true };
  }

  if (err instanceof FluviusMandatesError) {
    // A 401 that reaches here is a JSON passthrough from upstream (Fluvius), not an expired
    // session — portalsFetch turns real auth failures into SessionExpiredError. So no
    // log-in action; fall through to the backend message / generic copy.
    if (hasCompanyStatusMapping && err.status === 400) return plain(t('errorInvalidCompany'));
    if (hasCompanyStatusMapping && err.status === 404) return plain(t('errorCompanyMissingKbo'));
    if (err.status === 403) return plain(t('errorNoCompanyAccess'));
    if (err.status === 502) return plain(t('errorFluviusUnreachable'));
    if (err.backendMessage) return plain(err.backendMessage);
  }

  return plain(t(fallbackKey));
};
